import api from './api'
import type {
  InvoiceSchedule,
  ScheduledInvoice,
  CreateInvoiceScheduleData,
  UpdateInvoiceScheduleData,
  InvoiceSchedulePreview,
  InvoiceScheduleProjection,
  ApiResponse,
  InvoiceScheduleStatistics
} from '@crm-src/types/invoice-schedule'

// Invoice Schedules API
export const invoiceSchedulesApi = {
  // Get all invoice schedules with optional filters
  getAll: async (params?: {
    page?: number
    per_page?: number
    status?: string
    customer_id?: string
    frequency?: string
    search?: string
  }): Promise<ApiResponse<InvoiceSchedule[]>> => {
    try {
      const response = await api.get('/invoice_schedules', { params })
      return {
        success: true,
        data: response.data.data,
        meta: response.data.meta
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch invoice schedules']
      }
    }
  },

  // Get single invoice schedule
  getById: async (id: string): Promise<ApiResponse<InvoiceSchedule>> => {
    try {
      const response = await api.get(`/invoice_schedules/${id}`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch invoice schedule']
      }
    }
  },

  // Create new invoice schedule
  create: async (data: CreateInvoiceScheduleData): Promise<ApiResponse<InvoiceSchedule>> => {
    try {
      const response = await api.post('/invoice_schedules', {
        invoice_schedule: data
      })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Invoice schedule created successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to create invoice schedule']
      }
    }
  },

  // Update invoice schedule
  update: async (id: string, data: UpdateInvoiceScheduleData): Promise<ApiResponse<InvoiceSchedule>> => {
    try {
      const response = await api.patch(`/invoice_schedules/${id}`, {
        invoice_schedule: data
      })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Invoice schedule updated successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to update invoice schedule']
      }
    }
  },

  // Delete (cancel) invoice schedule
  delete: async (id: string): Promise<ApiResponse<{ id: string }>> => {
    try {
      const response = await api.delete(`/invoice_schedules/${id}`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Invoice schedule cancelled successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to cancel invoice schedule']
      }
    }
  },

  // Pause invoice schedule
  pause: async (id: string): Promise<ApiResponse<InvoiceSchedule>> => {
    try {
      const response = await api.post(`/invoice_schedules/${id}/pause`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Invoice schedule paused successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to pause invoice schedule']
      }
    }
  },

  // Resume invoice schedule
  resume: async (id: string): Promise<ApiResponse<InvoiceSchedule>> => {
    try {
      const response = await api.post(`/invoice_schedules/${id}/resume`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Invoice schedule resumed successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to resume invoice schedule']
      }
    }
  },

  // Preview upcoming occurrences
  preview: async (id: string, count?: number): Promise<ApiResponse<InvoiceSchedulePreview>> => {
    try {
      const response = await api.get(`/invoice_schedules/${id}/preview`, {
        params: { count }
      })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch preview']
      }
    }
  },

  // Get schedule by source invoice ID
  getBySourceInvoice: async (sourceInvoiceId: string): Promise<ApiResponse<InvoiceSchedule | null>> => {
    try {
      const response = await api.get('/invoice_schedules', {
        params: { source_invoice_id: sourceInvoiceId }
      })
      const schedules = response.data.data || []
      // Return the first active/paused schedule (not cancelled)
      const activeSchedule = schedules.find((s: InvoiceSchedule) =>
        s.status === 'active' || s.status === 'paused'
      )
      return {
        success: true,
        data: activeSchedule || null
      }
    } catch (error: any) {
      return {
        success: false,
        data: null,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch invoice schedule']
      }
    }
  },

  // Get upcoming scheduled invoices across all schedules
  getUpcoming: async (params?: {
    limit?: number
    days?: number
    customer_id?: string
  }): Promise<ApiResponse<ScheduledInvoice[]>> => {
    try {
      const response = await api.get('/invoice_schedules/upcoming', { params })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
        meta: response.data.meta
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch upcoming invoices']
      }
    }
  },

  // Forward-looking projection across all ACTIVE schedules — one date-sorted
  // list of upcoming occurrences within a horizon window (`days`, default 90).
  // Powers the Recurring Invoices "Timeline" view.
  projection: async (params?: {
    days?: number
    customer_id?: string
  }): Promise<ApiResponse<InvoiceScheduleProjection>> => {
    try {
      const response = await api.get('/invoice_schedules/projection', { params })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch projection']
      }
    }
  }
}

// Scheduled Invoices API
export const scheduledInvoicesApi = {
  // Get all scheduled invoices with optional filters
  getAll: async (params?: {
    page?: number
    per_page?: number
    status?: string
    schedule_id?: string
    customer_id?: string
    from_date?: string
    to_date?: string
  }): Promise<ApiResponse<ScheduledInvoice[]> & { statistics?: InvoiceScheduleStatistics }> => {
    try {
      const response = await api.get('/scheduled_invoices', { params })
      return {
        success: true,
        data: response.data.data,
        meta: response.data.meta,
        statistics: response.data.statistics
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch scheduled invoices']
      }
    }
  },

  // Get single scheduled invoice
  getById: async (id: string): Promise<ApiResponse<ScheduledInvoice>> => {
    try {
      const response = await api.get(`/scheduled_invoices/${id}`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch scheduled invoice']
      }
    }
  },

  // Cancel scheduled invoice
  cancel: async (id: string): Promise<ApiResponse<ScheduledInvoice>> => {
    try {
      const response = await api.post(`/scheduled_invoices/${id}/cancel`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Scheduled invoice cancelled successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to cancel scheduled invoice']
      }
    }
  },

  // Preview scheduled invoice
  preview: async (id: string): Promise<ApiResponse<{
    scheduled_invoice: ScheduledInvoice
    preview: {
      issue_date: string
      due_date: string
      recipient_email: string
      recipient_name: string
      source_invoice: {
        id: string
        invoice_number: string
        total_amount: number
        products?: any[]
      }
    }
  }>> => {
    try {
      const response = await api.get(`/scheduled_invoices/${id}/preview`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to fetch preview']
      }
    }
  },

  // Delete scheduled invoice
  delete: async (id: string): Promise<ApiResponse<{ id: string }>> => {
    try {
      const response = await api.delete(`/scheduled_invoices/${id}`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Scheduled invoice deleted successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to delete scheduled invoice']
      }
    }
  },

  // Bulk delete scheduled invoices
  bulkDelete: async (ids: string[]): Promise<ApiResponse<{
    deleted_count: number
    skipped_count: number
    deleted_ids: string[]
  }>> => {
    try {
      const response = await api.delete('/scheduled_invoices/bulk_destroy', { data: { ids } })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Scheduled invoices deleted successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to delete scheduled invoices']
      }
    }
  }
}

// Helper functions
export function formatFrequency(frequency: string, interval: number = 1): string {
  const baseFrequency = frequency.charAt(0).toUpperCase() + frequency.slice(1)
  if (interval === 1) {
    return baseFrequency
  }
  return `Every ${interval} ${frequency === 'weekly' ? 'weeks' : frequency === 'monthly' ? 'months' : frequency === 'quarterly' ? 'quarters' : 'years'}`
}

export function formatScheduledDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
